import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { ClipboardList, Car, AlertTriangle, CheckCircle2, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const PRIORITY_STYLE = {
  urgente: "bg-red-500/15 text-red-500",
  importante: "bg-orange-500/15 text-orange-500",
  monitorar: "bg-slate-500/15 text-slate-500",
};

export default function Inspecoes() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/inspections");
    setItems(data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-5" data-testid="inspecoes-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Qualidade e diagnóstico</div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Inspeções</h1>
          <p className="text-sm text-muted-foreground mt-1">Checklist completo por Job com prioridades e PDF profissional.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-inspection-btn"><Plus className="w-4 h-4" /> Nova Inspeção</Button>
          </DialogTrigger>
          <NovaInspecaoDialog onCreated={(id) => { setOpen(false); navigate(`/inspecoes/${id}`); }} />
        </Dialog>
      </div>

      {loading ? <div className="text-sm text-muted-foreground">Carregando…</div> :
        items.length === 0 ? <div className="border border-dashed border-border rounded-md p-12 text-center text-sm text-muted-foreground">Nenhuma inspeção realizada.</div> :
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="inspecoes-grid">
          {items.map((i) => (
            <button key={i.id} onClick={() => navigate(`/inspecoes/${i.id}`)}
              className="text-left border border-border rounded-md bg-card overflow-hidden hover:-translate-y-0.5 hover:shadow-sm transition-all"
              data-testid={`inspection-card-${i.id}`}>
              <div className="h-28 bg-secondary relative">
                {i.vehicle?.photo_url ? <img src={i.vehicle.photo_url} alt="" className="w-full h-full object-cover" /> :
                  <div className="w-full h-full flex items-center justify-center"><Car className="w-6 h-6 text-muted-foreground" /></div>}
                <div className={`absolute top-2 left-2 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-medium ${PRIORITY_STYLE[i.overall_priority || "monitorar"]}`}>
                  {i.overall_priority || "monitorar"}
                </div>
                {i.finalized && <div className="absolute top-2 right-2 px-2 py-0.5 rounded bg-emerald-600/90 text-white text-[10px] uppercase tracking-wider">Finalizada</div>}
              </div>
              <div className="p-3">
                <div className="font-display font-semibold text-sm">{i.vehicle?.year} {i.vehicle?.make} {i.vehicle?.model}</div>
                <div className="text-xs text-muted-foreground truncate">{i.client?.name} · {i.job?.service_type}</div>
                <div className="flex gap-2 mt-3 text-[11px]">
                  <span className="inline-flex items-center gap-1 text-emerald-500"><CheckCircle2 className="w-3 h-3" />{i.counts.total - i.counts.atencao - i.counts.urgente} OK</span>
                  {i.counts.atencao > 0 && <span className="inline-flex items-center gap-1 text-orange-500"><AlertTriangle className="w-3 h-3" />{i.counts.atencao}</span>}
                  {i.counts.urgente > 0 && <span className="inline-flex items-center gap-1 text-red-500"><AlertTriangle className="w-3 h-3" />{i.counts.urgente}</span>}
                </div>
              </div>
            </button>
          ))}
        </div>}
    </div>
  );
}

function NovaInspecaoDialog({ onCreated }) {
  const [jobs, setJobs] = useState([]);
  const [jobId, setJobId] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/jobs").then(({ data }) => setJobs(data.filter((j) => j.status !== "entregue")));
  }, []);

  const submit = async () => {
    if (!jobId) { toast.error("Selecione um Job"); return; }
    setSaving(true);
    try {
      const { data } = await api.get(`/jobs/${jobId}/inspection`);
      toast.success("Inspeção pronta");
      onCreated(data.id);
    } catch { toast.error("Erro ao criar inspeção"); }
    finally { setSaving(false); }
  };

  return (
    <DialogContent className="sm:max-w-lg" data-testid="new-inspection-dialog">
      <DialogHeader><DialogTitle className="font-display tracking-tight">Nova Inspeção</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div className="text-sm text-muted-foreground flex items-start gap-2">
          <ClipboardList className="w-4 h-4 mt-0.5" />
          Escolha o Job para o qual você quer executar a inspeção. Se já existir uma, você será levado a ela.
        </div>
        <div>
          <Select value={jobId} onValueChange={setJobId}>
            <SelectTrigger data-testid="inspection-job-select"><SelectValue placeholder="Selecionar Job" /></SelectTrigger>
            <SelectContent>
              {jobs.map((j) => (
                <SelectItem key={j.id} value={j.id}>
                  {j.vehicle?.year} {j.vehicle?.make} {j.vehicle?.model} · {j.client_name} · {j.service_type}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} data-testid="inspection-open-btn">{saving ? "Abrindo…" : "Abrir Checklist"}</Button>
        </DialogFooter>
      </div>
    </DialogContent>
  );
}
